/**
 * My Account wishlist — Wysh-backed rows matching Figma (image, size, qty, ATC, share).
 */
import { addToWysh, removeFromWysh, defaultWishlistKey } from './fluevog-wishlist.js';

const WYSH_CACHE_KEY = '_wysh-key';

const PRODUCTS_QUERY = `
  query getProductsByIds($ids: [ID!]!, $country: CountryCode, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    nodes(ids: $ids) {
      ... on Product {
        id
        title
        handle
        availableForSale
        featuredImage { url }
        vendor
        priceRange {
          minVariantPrice { amount currencyCode }
        }
        variants(first: 100) {
          edges {
            node {
              id
              title
              availableForSale
              image { url }
              price { amount currencyCode }
              selectedOptions { name value }
            }
          }
        }
      }
    }
  }
`;

function readWishlists() {
  try {
    const raw = localStorage.getItem(WYSH_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed?.wishlists)) return parsed.wishlists;
    if (Array.isArray(parsed)) return parsed;
  } catch {
    /* ignore */
  }
    return [];
}

function collectEntries(wishlists) {
  const entries = [];
  wishlists.forEach((list) => {
    const items = list?.items && typeof list.items === 'object' ? list.items : {};
    Object.keys(items).forEach((productId) => {
      const item = items[productId] || {};
      const variantIds = Array.isArray(item.variant_ids)
        ? item.variant_ids.map(String)
        : [];
      entries.push({
        productId: String(productId),
        variantIds,
        wishlistKey: list.wishlist_key || null,
      });
    });
  });
  return entries;
}

function getStorefrontToken() {
  try {
    const el = document.getElementById('shopify-features');
    return el ? JSON.parse(el.textContent).accessToken || '' : '';
  } catch {
    return '';
  }
}

async function fetchProducts(productIds) {
  if (!productIds.length) return [];
  const token = getStorefrontToken();
  if (!token) return [];

  const country = (window.wysh_country || window.Shopify?.country || 'US').toUpperCase();
  const language = (
    window.wysh_language ||
    window.Shopify?.locale?.split('-')[0] ||
    'EN'
  ).toUpperCase();

  const response = await fetch('/api/2025-10/graphql.json', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': token,
    },
    body: JSON.stringify({
      query: PRODUCTS_QUERY,
      variables: {
        ids: productIds.map((id) => `gid://shopify/Product/${id}`),
        country,
        language,
      },
    }),
  });

  if (!response.ok) throw new Error(`Wishlist product fetch failed: ${response.status}`);
  const json = await response.json();
  if (json.errors?.length) throw new Error('Wishlist GraphQL query failed');
  return (json.data?.nodes || []).filter(Boolean);
}

async function fetchProductFamilies(handles) {
  const unique = [...new Set(handles.filter(Boolean))];
  const entries = await Promise.all(
    unique.map(async (handle) => {
      try {
        const response = await fetch(
          `/products/${encodeURIComponent(handle)}?view=fv-wishlist-data`,
          { headers: { Accept: 'application/json' } }
        );
        if (!response.ok) return [handle, ''];
        const text = await response.text();
        const json = JSON.parse(text.trim());
        return [handle, json.family || ''];
      } catch {
        return [handle, ''];
      }
    })
  );
  return new Map(entries);
}

function displayTitle(title) {
  const raw = String(title || '');
  return raw.split(' - ')[0] || raw;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function pickVariant(product, variantIds) {
  const variants = product.variants?.edges?.map((edge) => edge.node) || [];
  if (!variants.length) return { variant: null, sizeSelected: false, variants };

  if (variantIds.length) {
    const match = variants.find((variant) =>
      variantIds.includes(String(variant.id.split('/').pop()))
    );
    if (match) return { variant: match, sizeSelected: true, variants };
  }

  const fallback = variants.find((variant) => variant.availableForSale) || variants[0];
  return { variant: fallback, sizeSelected: false, variants };
}

function sizeChoices(variants) {
  const seen = new Set();
  const choices = [];
  variants.forEach((variant) => {
    const sizeOption = (variant.selectedOptions || []).find((option) =>
      (option.name || '').toLowerCase().includes('size')
    );
    if (!sizeOption?.value || sizeOption.value === 'Default Title') return;
    if (seen.has(sizeOption.value)) return;
    seen.add(sizeOption.value);
    choices.push({
      label: sizeOption.value,
      variantId: String(variant.id.split('/').pop()),
      available: Boolean(variant.availableForSale),
    });
  });
  return choices;
}

function iconFromTemplates(templates, name) {
  const template = templates.find((node) => node.dataset.fvWishlistIcon === name);
  const svg = template?.content?.querySelector('svg');
  return svg ? svg.outerHTML : '';
}

function shareUrls(product, imageUrl) {
  const origin = window.location.origin;
  const url = `${origin}/products/${product.handle}`;
  const title = displayTitle(product.title || '');
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedImage = encodeURIComponent(imageUrl || '');
  return {
    email: `mailto:?subject=${encodedTitle}&body=${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&media=${encodedImage}&description=${encodedTitle}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
  };
}

class FluevogAccountWishlist extends HTMLElement {
  connectedCallback() {
    this._iconTemplates = [...this.querySelectorAll('template[data-fv-wishlist-icon]')].map((node) =>
      node.cloneNode(true)
    );
    this._onStorage = (event) => {
      if (!event.key || event.key === WYSH_CACHE_KEY) this.render();
    };
    window.addEventListener('storage', this._onStorage);
    this.addEventListener('click', this._onClick);
    this.addEventListener('change', this._onChange);
    this.render();
  }

  disconnectedCallback() {
    window.removeEventListener('storage', this._onStorage);
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('change', this._onChange);
  }

  _onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const qtyBtn = target.closest('[data-fv-qty]');
    if (qtyBtn) {
      event.preventDefault();
      this._adjustQty(qtyBtn);
      return;
    }

    const addBtn = target.closest('[data-fv-add-to-cart]');
    if (addBtn) {
      event.preventDefault();
      this._addToCart(addBtn);
      return;
    }

    const removeBtn = target.closest('[data-fv-wishlist-remove]');
    if (removeBtn) {
      event.preventDefault();
      this._removeItem(removeBtn);
    }
  };

  _onChange = async (event) => {
    const select = event.target instanceof HTMLSelectElement ? event.target : null;
    if (!select?.matches('[data-fv-size-select]')) return;
    const row = select.closest('[data-fv-wishlist-row]');
    if (!row) return;

    const addBtn = row.querySelector('[data-fv-add-to-cart]');
    const removeBtn = row.querySelector('[data-fv-wishlist-remove]');
    const productId = row.dataset.productId || '';
    const variantId = select.value;
    const previousVariantId =
      select.dataset.previousVariantId || removeBtn?.dataset.variantId || '';

    if (addBtn) {
      addBtn.dataset.variantId = variantId;
      addBtn.disabled = !variantId;
    }

    if (!productId || !variantId || variantId === previousVariantId) return;

    select.disabled = true;
    try {
      const wishlistKey = defaultWishlistKey();
      if (!wishlistKey) throw new Error('No wishlist key found');

      // Add the new size first so the item never briefly disappears from Wysh.
      await addToWysh(productId, variantId, wishlistKey);
      if (previousVariantId) {
        await removeFromWysh(productId, previousVariantId, wishlistKey);
      }

      if (removeBtn) removeBtn.dataset.variantId = variantId;
      select.dataset.previousVariantId = variantId;
    } catch (error) {
      console.error(error);
      if (previousVariantId) {
        select.value = previousVariantId;
        if (addBtn) {
          addBtn.dataset.variantId = previousVariantId;
          addBtn.disabled = false;
        }
      } else {
        select.value = '';
        if (addBtn) {
          addBtn.dataset.variantId = '';
          addBtn.disabled = true;
        }
      }
    } finally {
      select.disabled = false;
    }
  };

  _adjustQty(button) {
    const row = button.closest('[data-fv-wishlist-row]');
    const input = row?.querySelector('[data-fv-qty-value]');
    if (!input) return;
    const delta = Number(button.dataset.fvQty) || 0;
    const next = Math.max(1, (Number(input.textContent) || 1) + delta);
    input.textContent = String(next);
  }

  async _addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    const row = button.closest('[data-fv-wishlist-row]');
    const qty = Number(row?.querySelector('[data-fv-qty-value]')?.textContent) || 1;
    const cartItems = document.querySelector('cart-items-component[data-drawer]');
    const sectionId = cartItems?.dataset?.sectionId;
    button.disabled = true;

    try {
      const payload = {
        items: [{ id: Number(variantId), quantity: qty }],
      };
      if (sectionId) {
        payload.sections = sectionId;
        payload.sections_url = window.location.pathname;
      }

      const addUrl =
        window.Theme?.routes?.cart_add_url ||
        `${window.Shopify?.routes?.root || '/'}cart/add.js`;
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.status || data.errors) {
        throw new Error(data.description || data.message || 'Could not add to cart');
      }

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: 'fv-account-wishlist',
            data: {
              source: 'fv-account-wishlist',
              itemCount: data.item_count,
              sections: data.sections,
            },
          },
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      button.disabled = false;
    }
  }

  async _removeItem(button) {
    const productId = button.dataset.productId;
    const variantId = button.dataset.variantId || '';
    if (!productId || button.disabled) return;

    // Reuse fluevog-wishlist Wysh remove by synthesizing a control click.
    const host = document.createElement('fluevog-wishlist');
    host.dataset.productId = productId;
    if (variantId) host.dataset.variantId = variantId;
    host.hidden = true;
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.setAttribute('ref', 'button');
    host.appendChild(trigger);
    document.body.appendChild(host);

    // Wait a tick for connectedCallback
    await Promise.resolve();
    try {
      if (typeof host.onClick === 'function') {
        await host.onClick({
          preventDefault() {},
          stopPropagation() {},
        });
      } else {
        trigger.click();
        await new Promise((resolve) => setTimeout(resolve, 400));
      }
    } catch (error) {
      console.error(error);
    } finally {
      host.remove();
      this.render();
    }
  }

  renderEmpty() {
    const emptyMessage = this.dataset.emptyMessage || 'Your wishlist is empty.';
    const shopUrl = this.dataset.shopUrl || '/collections/all';
    const shopLabel = this.dataset.shopLabel || 'Continue shopping';
    this.classList.add('is-empty');
    this.closest('.fv-account-wishlist__box')?.classList.add('is-empty');
    this.innerHTML = `
      <p class="fv-account-panel__empty">${escapeHtml(emptyMessage)}</p>
      <p>
        <a class="fv-account-panel__link" href="${escapeHtml(shopUrl)}">${escapeHtml(shopLabel)}</a>
      </p>
    `;
    (this._iconTemplates || []).forEach((template) => this.appendChild(template.cloneNode(true)));
  }

  async render() {
    if (!this._iconTemplates?.length) {
      this._iconTemplates = [...this.querySelectorAll('template[data-fv-wishlist-icon]')].map((node) =>
        node.cloneNode(true)
      );
    }

    const loadingMessage = this.dataset.loadingMessage || 'Loading wishlist…';
    this.innerHTML = `<p class="fv-account-wishlist__loading">${escapeHtml(loadingMessage)}</p>`;
    this._iconTemplates.forEach((template) => this.appendChild(template.cloneNode(true)));

    const entries = collectEntries(readWishlists());
    if (!entries.length) {
      this.renderEmpty();
      return;
    }

    try {
      const products = await fetchProducts(entries.map((entry) => entry.productId));
      const familiesByHandle = await fetchProductFamilies(products.map((p) => p.handle));
      const byId = new Map(
        products.map((product) => [String(product.id.split('/').pop()), product])
      );

      const labels = {
        size: this.dataset.sizeLabel || 'Size',
        quantity: this.dataset.quantityLabel || 'Quantity',
        addToCart: this.dataset.addToCartLabel || 'Add to Cart',
        remove: this.dataset.removeLabel || 'Remove',
        email: this.dataset.shareEmailLabel || 'Email',
        facebook: this.dataset.shareFacebookLabel || 'Share on Facebook',
        pinterest: this.dataset.sharePinterestLabel || 'Share on Pinterest',
        twitter: this.dataset.shareTwitterLabel || 'Share on Twitter',
      };

      const cartIcon = iconFromTemplates(this._iconTemplates, 'cart');
      const emailIcon = iconFromTemplates(this._iconTemplates, 'email');
      const facebookIcon = iconFromTemplates(this._iconTemplates, 'facebook');
      const pinterestIcon = iconFromTemplates(this._iconTemplates, 'pinterest');
      const twitterIcon = iconFromTemplates(this._iconTemplates, 'twitter');
      const closeIcon = iconFromTemplates(this._iconTemplates, 'close');

      const rows = entries
        .map((entry) => {
          const product = byId.get(entry.productId);
          if (!product) return '';

          const { variant, sizeSelected, variants } = pickVariant(product, entry.variantIds);
          const choices = sizeChoices(variants);
          // Only preselect a size when Wysh stored a variant — don't invent one.
          const selectedVariantId = sizeSelected ? String(variant.id.split('/').pop()) : '';
          const image = variant?.image?.url || product.featuredImage?.url || '';
          const href = selectedVariantId
            ? `/products/${product.handle}?variant=${selectedVariantId}`
            : `/products/${product.handle}`;
          const title = displayTitle(product.title || '');
          const family = familiesByHandle.get(product.handle) || product.vendor || '';
          const shares = shareUrls(product, image);
          const canAdd = Boolean(selectedVariantId);

          const placeholderOption = sizeSelected
            ? ''
            : `<option value="" selected disabled>Select</option>`;
          const sizeOptions = choices.length
            ? `${placeholderOption}${choices
                .map(
                  (choice) =>
                    `<option value="${escapeHtml(choice.variantId)}" ${
                      choice.variantId === selectedVariantId ? 'selected' : ''
                    } ${choice.available ? '' : 'disabled'}>${escapeHtml(choice.label)}</option>`
                )
                .join('')}`
            : `<option value="${escapeHtml(selectedVariantId)}" selected>—</option>`;

        return `
            <article
              class="fv-account-wishlist__row"
              data-fv-wishlist-row
              data-product-id="${escapeHtml(entry.productId)}"
            >
              <button
                type="button"
                class="fv-account-wishlist__remove"
                data-fv-wishlist-remove
                data-product-id="${escapeHtml(entry.productId)}"
                data-variant-id="${escapeHtml(sizeSelected ? selectedVariantId : '')}"
                aria-label="${escapeHtml(labels.remove)} ${escapeHtml(title)}"
              >
                ${closeIcon || '<span aria-hidden="true">×</span>'}
              </button>

              <div class="fv-account-wishlist__main">
                <a class="fv-account-wishlist__media" href="${escapeHtml(href)}">
                  ${
                    image
                      ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(
                          title
                        )}" width="140" height="140" loading="lazy">`
                      : ''
                  }
                </a>

                <div class="fv-account-wishlist__info">
                  ${
                    family
                      ? `<p class="fv-account-wishlist__family">${escapeHtml(family)}</p>`
                      : ''
                  }
                  <a class="fv-account-wishlist__title" href="${escapeHtml(href)}">${escapeHtml(
                    title
                  )}</a>
                </div>

                <div class="fv-account-wishlist__controls">
                  <label class="fv-account-wishlist__field">
                    <span class="fv-account-wishlist__field-label">${escapeHtml(labels.size)}</span>
                    <span class="fv-account-wishlist__select-wrap">
                      <select
                        class="fv-account-wishlist__select"
                        data-fv-size-select
                        data-previous-variant-id="${escapeHtml(selectedVariantId)}"
                        ${choices.length ? '' : 'disabled'}
                      >
                        ${sizeOptions}
                      </select>
                    </span>
                  </label>

                  <div class="fv-account-wishlist__field">
                    <span class="fv-account-wishlist__field-label">${escapeHtml(labels.quantity)}</span>
                    <div class="fv-account-wishlist__qty" role="group" aria-label="${escapeHtml(
                      labels.quantity
                    )}">
                      <button type="button" class="fv-account-wishlist__qty-btn" data-fv-qty="-1" aria-label="Decrease quantity">−</button>
                      <span class="fv-account-wishlist__qty-value" data-fv-qty-value>1</span>
                      <button type="button" class="fv-account-wishlist__qty-btn" data-fv-qty="1" aria-label="Increase quantity">+</button>
                    </div>
                  </div>

                  <button
                    type="button"
                    class="fv-account-wishlist__atc"
                    data-fv-add-to-cart
                    data-variant-id="${escapeHtml(selectedVariantId)}"
                    ${canAdd ? '' : 'disabled'}
                  >
                    ${cartIcon ? `<span class="fv-account-wishlist__atc-icon" aria-hidden="true">${cartIcon}</span>` : ''}
                    <span>${escapeHtml(labels.addToCart)}</span>
                  </button>
                </div>
              </div>

              <div class="fv-account-wishlist__share">
                <a class="fv-account-wishlist__share-link" href="${escapeHtml(shares.email)}">
                  <span class="fv-account-wishlist__share-icon" aria-hidden="true">${emailIcon}</span>
                  <span>${escapeHtml(labels.email)}</span>
                </a>
                <a class="fv-account-wishlist__share-link" href="${escapeHtml(
                  shares.facebook
                )}" target="_blank" rel="noopener noreferrer">
                  <span class="fv-account-wishlist__share-icon" aria-hidden="true">${facebookIcon}</span>
                  <span>${escapeHtml(labels.facebook)}</span>
                </a>
                <a class="fv-account-wishlist__share-link" href="${escapeHtml(
                  shares.pinterest
                )}" target="_blank" rel="noopener noreferrer">
                  <span class="fv-account-wishlist__share-icon" aria-hidden="true">${pinterestIcon}</span>
                  <span>${escapeHtml(labels.pinterest)}</span>
                </a>
                <a class="fv-account-wishlist__share-link" href="${escapeHtml(
                  shares.twitter
                )}" target="_blank" rel="noopener noreferrer">
                  <span class="fv-account-wishlist__share-icon" aria-hidden="true">${twitterIcon}</span>
                  <span>${escapeHtml(labels.twitter)}</span>
                </a>
              </div>
            </article>
          `;
        })
        .filter(Boolean)
      .join('');

      if (!rows) {
        this.renderEmpty();
        return;
      }

      this.classList.remove('is-empty');
      this.closest('.fv-account-wishlist__box')?.classList.remove('is-empty');

      // Keep icon templates available for later re-renders
      this.innerHTML = `<div class="fv-account-wishlist__list">${rows}</div>`;
      this._iconTemplates.forEach((template) => this.appendChild(template.cloneNode(true)));
    } catch (error) {
      console.error(error);
      this.renderEmpty();
    }
  }
}

if (!customElements.get('fluevog-account-wishlist')) {
  customElements.define('fluevog-account-wishlist', FluevogAccountWishlist);
}
